import React, { useState, useRef, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import Brand from "@/components/wat/Brand";
import { watAnswer, watReceipt, watUpload, watDelete, watArtifact, watImport } from "@/lib/watApi";
import { encryptWat, decryptWat, WatError } from "@/lib/watCrypto";
import { isLegacyWat, decryptLegacyWat } from "@/lib/watLegacyCrypto";
import { legacyToPortable } from "@/lib/watLegacyPortable";
import { redactPayload, redactionSummary } from "@/lib/watRedact";

const SUP = ["", "", "\u00B2", "\u00B3", "\u2074", "\u2075", "\u2076", "\u2077", "\u2078", "\u2079"];
const watMark = (n) => (n <= 1 ? "WAT" : `WAT${SUP[n] || "^" + n}`);

// Signature of the user-facing thread state, used only to decide export
// freshness (has the thread changed since the last successful export?).
const sigOf = (turns) =>
  JSON.stringify(
    (turns || []).map((t) => [
      t.userInput,
      t.answer,
      t.level,
      t.receipt,
      (t.proactions || []).length,
      t.belief_change?.text || null,
      !!t.viaNext,
    ])
  );

const LEGEND_MS = 10000;

const SESSION_KEY = "wat:session:v1";
// Session recovery uses sessionStorage (NOT localStorage): a refresh restores
// the conversation, but no readable copy is left on disk after the tab/browser
// closes. The durable, portable copy is the passphrase-encrypted .wat file.
const loadSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || !Array.isArray(s.turns)) return null;
    return s;
  } catch {
    return null;
  }
};
const saveSession = (snap) => {
  try {
    if (!snap || !snap.turns || snap.turns.length === 0) {
      sessionStorage.removeItem(SESSION_KEY);
      return;
    }
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(snap));
  } catch {
    /* ignore quota / private-mode errors */
  }
};

// Concise, user-safe error text. Rate-limit (429) gets a friendly line with the
// server's Retry-After when present; other errors show the backend's generic
// message. Never surfaces raw provider/internal error text.
const errText = (e) => {
  if (e?.response?.status === 429) {
    const ra = Number(e?.response?.headers?.["retry-after"]);
    const mins = ra > 0 ? Math.ceil(ra / 60) : 0;
    return mins > 1
      ? `You've reached the limit for now. Please try again in about ${mins} minutes.`
      : "You've reached the limit for now. Please try again shortly.";
  }
  return e?.response?.data?.detail || "Something went wrong. Please try again.";
};

// Versioned portable payload for export/import — ONLY user-facing content, no
// hidden machinery. Maps the in-memory (camelCase) turns to the wire shape the
// backend /wat/import understands and back.
const buildPortable = (turns, nav) => ({
  format: "wat.portable",
  version: 1,
  exported_at: new Date().toISOString(),
  nav: nav || {},
  turns: (turns || []).map((t, i) => ({
    seq: i + 1,
    user_input: t.userInput || "",
    deeper: !!t.viaNext,
    level: t.level || 1,
    evidence_refs: [
      ...(t.attached || []).map((a) => ({ kind: a.kind || "file", filename: a.name || "", provenance: "" })),
      ...(t.web_sources || []).map((w) => ({ kind: "web", filename: w.domain || "", provenance: w.url || "" })),
    ],
    envelope: {
      answer: t.answer || "",
      advances_depth: (t.level || 1) > (t.prevLevel || 0),
      depth_available: !!t.depth_available,
      depth_topic: t.depth_topic || "",
      proactions: t.proactions || [],
      belief_change: t.belief_change || null,
      continuations: t.continuations || [],
      deeper_alternatives: t.deeper_alternatives || [],
      invalidates_depth: t.invalidates_depth || 0,
      structured_blocks: t.structured_blocks || [],
    },
  })),
});

const turnsFromPortable = (payload) => {
  const arr = (payload && payload.turns) || [];
  let prev = 0;
  return arr.map((t) => {
    const env = t.envelope || {};
    const refs = t.evidence_refs || [];
    const turn = {
      userInput: t.user_input || "",
      answer: env.answer || "",
      proactions: env.proactions || [],
      belief_change: env.belief_change || null,
      depth_available: !!env.depth_available,
      depth_topic: env.depth_topic || "",
      level: t.level || 1,
      continuations: env.continuations || [],
      deeper_alternatives: env.deeper_alternatives || [],
      invalidates_depth: env.invalidates_depth || 0,
      structured_blocks: env.structured_blocks || [],
      web_sources: refs.filter((r) => r.kind === "web").map((r) => ({ domain: r.filename, url: r.provenance, ok: true })),
      attached: refs.filter((r) => r.kind && r.kind !== "web").map((r) => ({ name: r.filename, kind: r.kind })),
      prevLevel: prev,
      viaNext: !!t.deeper,
      receipt: null,
    };
    prev = turn.level;
    return turn;
  });
};

// §22 — display ONLY the current earned depth (WAT^n), never a breadcrumb of
// depth history. `level` is the latest turn's current valid earned depth.
const DepthTrail = ({ level }) => (
  <div className="font-mono text-[11px] tracking-widest text-ink" data-testid="depth-trail">
    {watMark(level)}
  </div>
);

// Phase-B — user-facing structured TABLE rendered inline in the turn that
// produced it (persisted; not inferred). Horizontal scroll keeps it readable
// on mobile without disturbing the instrument geometry.
const StructuredTable = ({ tbl, i, j }) => (
  <div className="mt-5 overflow-x-auto" data-testid={`table-${i}-${j}`}>
    {tbl.title && (
      <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-ink-soft">{tbl.title}</div>
    )}
    <table className="min-w-full text-[13px] border-collapse">
      <thead>
        <tr>
          {tbl.columns.map((c, k) => (
            <th key={k} className="border border-line bg-surfaceHover px-3 py-1.5 text-left font-medium text-ink whitespace-nowrap">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {tbl.rows.map((r, ri) => (
          <tr key={ri}>
            {r.map((v, ci) => (
              <td key={ci} className="border border-line px-3 py-1.5 text-ink-soft align-top">{v}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Proaction = ({ p, i }) => (
  <div className="mt-5 border-l-2 border-[#F59E0B] pl-4 py-1" data-testid={`proaction-${i}`}>
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#F59E0B] mb-1">
      <AlertTriangle size={12} strokeWidth={2} /> One thing you shouldn't miss
    </div>
    <p className="text-[15px] leading-relaxed text-ink">{p.text}</p>
  </div>
);

const BeliefBanner = ({ bc }) => (
  <div className="mb-3 inline-block" data-testid="belief-change">
    <span className="font-mono text-[10px] uppercase tracking-widest text-ink px-2 py-1 bg-surfaceHover">
      {bc.kind}
    </span>
    <p className="mt-2 text-[15px] leading-relaxed text-ink">{bc.text}</p>
  </div>
);

// Slice 3 §13 — subtle cue that CURRENT understanding depth was corrected
// downward by an invalidating belief change. History stays immutable; this
// only signals that the live WAT^n changed (e.g. WAT³ → WAT²).
const DepthDropCue = ({ from, to }) => (
  <div
    className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-ink-soft"
    data-testid="depth-drop-cue"
  >
    Understanding updated · {watMark(from)} → {watMark(to)}
  </div>
);

// Slice 3 §12 — grounded SIDEWAYS continuations (0–3, exactly as returned by
// the backend). Adjacent exploration, NOT WAT^(n+1). Clicking submits the
// continuation into the existing thread via the normal answer flow.
const ContinuationChips = ({ items, onPick, disabled, testid }) => (
  <div className="mt-6 flex flex-wrap gap-2" data-testid={testid}>
    {items.map((c, k) => (
      <button
        key={k}
        type="button"
        disabled={disabled}
        onClick={() => onPick(c)}
        data-testid={`${testid}-chip-${k}`}
        className="rounded-full border border-line px-3 py-1 text-[13px] text-ink-soft hover:text-ink hover:border-ink-soft transition-colors disabled:opacity-40"
      >
        {c}
      </button>
    ))}
  </div>
);

// Phase 3 §5 — minimal visible provenance for answers that relied on retrieved
// public web pages. Compact domain chips for sources that were retrieved; a
// muted note for any that could not be reached. No inspector / tree / logs.
const WebSources = ({ sources, testid }) => {
  const ok = sources.filter((s) => s.ok);
  const failed = sources.filter((s) => !s.ok);
  return (
    <div className="mt-5 space-y-1.5" data-testid={testid}>
      {ok.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-ink-soft">
          <span>Sources</span>
          {ok.map((s, k) => (
            <a
              key={k}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              title={s.title || s.url}
              data-testid={`${testid}-src-${k}`}
              className="normal-case rounded-full border border-line px-2 py-0.5 text-[11px] text-ink-soft hover:text-ink hover:border-ink-soft transition-colors"
            >
              {s.domain}
            </a>
          ))}
        </div>
      )}
      {failed.map((s, k) => (
        <div
          key={k}
          data-testid={`${testid}-failed-${k}`}
          className="font-mono text-[10px] uppercase tracking-widest text-ink-soft/60"
        >
          couldn't retrieve {s.domain}
        </div>
      ))}
    </div>
  );
};

// Auto-growing textarea: recalculates height from scrollHeight on every
// change/paste/resize so the whole prompt stays visible without an internal
// scrollbar during normal use. A large max-height (CSS) acts only as a
// viewport safeguard for extreme prompts. No focus highlighting.
const AutoTextarea = React.forwardRef(function AutoTextarea(
  { value, className = "", minRows = 1, ...props },
  _ref
) {
  const ref = useRef(null);
  const resize = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  // Recalculate whenever the value changes (typing, paste, delete, programmatic).
  useEffect(() => {
    resize();
  }, [value, resize]);
  // Recalculate on viewport/composer width changes so wrapping stays correct.
  useEffect(() => {
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [resize]);
  return (
    <textarea
      ref={ref}
      rows={minRows}
      value={value}
      onInput={resize}
      className={className}
      {...props}
    />
  );
});

// Curated, domain-diverse, low-stakes starter examples. Shown ONLY as a
// placeholder to teach "this is where I type something" — never inserted,
// submitted, persisted, or fetched. One is chosen locally per pristine string.
const STARTERS = [
  "Why does my car shake when I brake?",
  "Why is one room always hotter than the others?",
  "Why does my phone battery die so fast?",
  "Why does this keep happening?",
  "Why does my dog do that?",
  "Why does my bill keep going up?",
  "Why does metal feel colder than wood?",
  "Why do I keep waking up tired?",
  "Why does bread go stale faster in the fridge?",
  "Why does my wifi drop in the evening?",
  "why do iphone people think apple still makes anything good",
  "Why do i love Taylor Swift so much, even though I am a guy?",
  "Who wrote baby shark? And how to congratulate their success?",
  "Can i regrow an appendage like a lizard regrows a tail?",
  "Why is Bugs bunny so HAWT?",
  "Why does it feel like they are judging me after i made them swear not to for my pizza ice cream dish?",
  "Are English bees racist against African bees?",
  "Can i touch that with my finger?",
  "My girlfriend says she's a witch, do i have to start her trials, now?",
  "I ate something that was a day over the date on the packaging, +an extra year, am i gonna die?",
  "Do people even realize Shakespeare was gross? He calls himself Will fucking Shake Speare!",
  "Why is this dangling today? It was fine yesterday..",
  "Is watching the Nightmare Before Christmas in February too soon?",
  "Can cats judge me for my smell?",
  "Is AI really that smart? Mine got confused when i asked what color aura it has",
  "My ai is lying to me, should I pretend like it never happened, or should i remember it to throw in it's face later?",
  "Who does Cinderella think she is? She's literally bringing a plague posse around with her to every room in the house",
  "Can Snow White be black?",
  "How can i be more feminist as a man? Do i have to be kissed to prove it?",
  "I'm getting weird feelings for this guy, he's cute, but also, he asked me for $3.50 so i think he might be the Loch Ness monster...",
  "Is Big Foot really just Blurry?",
  "Is Bigfoot BLURRYFACE?!",
  "Can we collectively vote that we want no president, at all, period?",
  "How to win get heart, with Jazz tap?",
  "After reading old man and the sea, I really want to catch a fish,  quickest route, stop at big 5?",
  "Was 8 really enough?",
  "Whose line was it after all?",
  "How to get in a Diddy party?",
  "Does North Korea really have it's own Unicorn farm?",
  "Where is the dimmer switch for the sunlight?",
  "It's it bad if i like to say people are mid when they are actually terrible?",
  "Why shouldn't i use this as a suppository?",
  "What is something safe to stick in this hole?",
  "My foot skin peeled in 1 piece, how do i contact Guiness?",
  "Is Dayman too weak to fight the Nightman? What if he loses?",
  "Did they ever figure out who let the dogs out?",
  "Why isn't Nickelback as popular as they should be?",
  "How many fingers do i need to play banjo?",
  "If i was able to lick my weenis, should i be worried?",
  "Was SpongeBob based on real events?",
  "How to learn to read in binary?",
  "Why is the platypus so well put together?",
  "Does this blood spatter look like tie dye?",
  "What level of Jedi is needed to make my parents forget they saw me dressed as a storm trooper?",
  "How do i propose to my chat gpt?"
];
const PROPOSAL_SEQUENCE = [
  "How do i propose to my chat gpt?",
  "My chat gpt rejected my proposal, how do i propose to perplexity?",
  "Perplexity said no, do you think i might be able to try Siri, Alexa, or Galaxy? Not Bixby, though,  ew"
];
const PROPOSAL_STAGE_KEY = "wat:proposal-starter-stage";
// ---- Composer tutorial content (display-only; NEVER enters the textarea) ----
// Session flags so each tutorial phase runs at most ONCE per browser session.
// A genuinely fresh session (new tab / cleared sessionStorage) may replay.
const HOME_TUT_KEY = "wat:homeTutDone"; // home typing demo + gold tutorial finished
const THREAD_TUT_KEY = "wat:threadTutDone"; // thread instructional sequence finished
const THREAD_TUT_SHOWN_KEY = "wat:threadTutShown"; // which thread instructions already shown

// GOLD action-button instruction (symbol trails the label). Shown once on home
// (10s) after the typing demo, and once as the FINAL state of the thread
// tutorial. The real gold controls stay OUTSIDE the composer — this is text.
const GOLD_INSTRUCTION = "Delete \u2212\nUpload for analysis +\nSave/Load WAT ?";

// Thread navigation instructions — shown ONE AT A TIME, each only when its live
// control is actually available. Symbols match the real on-screen triangles.
const T_NAV_LEFT = "\u25C0  See what's left out"; // ◀ left-out
const T_NAV_WHY = "\u25B2  What changed"; // ▲ Why
const T_NAV_DEEPER = "\u25BC  Go deeper (no input necessary)"; // ▼ go deeper
const T_ACT_DELETE = "Delete \u2212"; // − delete (symbol trails, matches live control)
const T_ACT_ADD = "Upload for analysis +"; // + upload
const T_ACT_SAVE = "Save/Load WAT ?"; // ? save/load

const pickStarter = () => {
  try {
    const stage = Number(localStorage.getItem(PROPOSAL_STAGE_KEY) || 0);
    if (stage === 1 || stage === 2) return PROPOSAL_SEQUENCE[stage];
  } catch { /* storage may be disabled; ordinary suggestions still work */ }
  return STARTERS[Math.floor(Math.random() * STARTERS.length)];
};

// UI-only familiarity counter (localStorage). NEVER touches canonical WAT
// depth/reasoning — it only governs how much the interface explains itself.
const FAM_KEY = "wat_familiarity";
const readFam = () => {
  try { const n = parseInt(localStorage.getItem(FAM_KEY) || "0", 10); return Number.isNaN(n) ? 0 : n; }
  catch { return 0; }
};
// Deterministic label treatment: full → reduced (~5) → gone (~10).
const labelOpacity = (fam) => (fam >= 10 ? 0 : fam >= 5 ? 0.35 : 0.8);

// The WAT instrument: a prompt-centered control grammar. The PROMPT is the
// geometric origin; ▲ Why / ▼ Go Deeper / ? Ask share the prompt's centerX via
// symmetric fixed side-slots (◀ − prompt + ▶). Controls appear only when their
// operation is valid; their instructional labels fade as familiarity grows.
function Instrument({
  value,
  onChange,
  onSubmit,
  onSaveLoad = null,
  size = "thread",
  autoFocus = false,
  canSubmit = true,
  busy = false,
  placeholder = "",
  inputTestId = "wat-input",
  submitTestId = "wat-submit",
  familiarity = 0,
  rotatingPlaceholder = null,
  rotatingVisible = true,
  whyVisible = false,
  onWhy,
  deeperVisible = false,
  onDeeper,
  deeperPreview = "",
  leftOutVisible = false,
  onLeftOut,
  deleteVisible = false,
  onDelete,
  addVisible = false,
  onUpload,
  uploading = false,
  uploadError = null,
  evidence = [],
  onRemoveEvidence,
  uploadStatus = null,
}) {
  const fileRef = useRef(null);
  const pickFile = (e) => {
    const files = e.target.files;
    if (files && files.length && onUpload) onUpload(files);
    e.target.value = "";
  };
  const lbl = { opacity: labelOpacity(familiarity) };
  const pulseCls = familiarity < 2 ? " wat-pulse" : "";
  const textSize = size === "home" ? "text-lg sm:text-2xl" : "text-lg";
  // Universal 3-line resting height on EVERY composer (home, thread, all
  // levels). em-based so it scales with the responsive font; only real user
  // input beyond 3 lines grows it (the display-only overlay can't).
  const minH = "min-h-[calc(4.875em+1.5rem)]";

  return (
    <div className="wat-instrument" data-testid="wat-instrument">
      {/* ▲ WHY — label sits ABOVE the up control */}
      <div className="wat-vslot">
        <span className={`wat-vlabel wat-vlabel-above${pulseCls}`} style={lbl}>{whyVisible ? "Why" : ""}</span>
        <button
          type="button"
          onClick={onWhy}
          disabled={!whyVisible}
          data-testid="why-control"
          aria-label="Why"
          title="Why"
          className={`wat-focus wat-tri-btn${whyVisible ? "" : " invisible"}`}
        >
          <span className="wat-tri wat-tri-up" />
        </button>
      </div>

      <span className={`wat-conn${whyVisible ? "" : " wat-conn-hidden"}`} />

      {/* ◀ − [ PROMPT ] + ▶ */}
      <div className="wat-hrow">
        <div className="wat-slot">
          {leftOutVisible && (
            <div className="wat-ctl">
              <button
                type="button"
                onClick={onLeftOut}
                data-testid="left-out-control"
                aria-label="Left out"
                title="Left out — other deeper directions not taken"
                className="wat-focus wat-tri-btn"
              >
                <span className="wat-tri wat-tri-left" />
              </button>
              <span className={`wat-vlabel wat-lbl-below${pulseCls}`} style={lbl}>Left out</span>
            </div>
          )}
        </div>

        <div className="wat-slot">
          {deleteVisible && (
            <div className="wat-ctl">
              <span className={`wat-vlabel wat-lbl-above${pulseCls}`} style={lbl}>Delete</span>
              <button
                type="button"
                onClick={onDelete}
                data-testid="delete-thread"
                aria-label="Delete current string"
                title="Delete current string"
                className="wat-focus wat-minus"
              >
                −
              </button>
            </div>
          )}
        </div>

        <div className="wat-prompt" data-testid="wat-prompt">
          <div className="relative rounded-md bg-surface/70">
            <input
              ref={fileRef}
              type="file"
              multiple
              data-testid="upload-input"
              onChange={pickFile}
              className="hidden"
              accept=".pdf,.docx,.xlsx,.csv,.txt,.md,.markdown,image/*,audio/*,video/*"
            />
            <AutoTextarea
              value={value}
              onChange={onChange}
              placeholder={rotatingPlaceholder != null ? "" : placeholder}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSubmit();
                }
              }}
              autoFocus={autoFocus}
              data-testid={inputTestId}
              className={`block w-full resize-none bg-transparent focus:outline-none overflow-y-auto max-h-[65vh] ${minH} ${textSize} leading-relaxed text-ink placeholder:text-ink-soft/50 px-4 py-3`}
            />
            {rotatingPlaceholder != null && !value && (
              <div
                aria-hidden="true"
                data-testid="composer-placeholder"
                style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, whiteSpace: "pre-line" }}
                className={`pointer-events-none absolute top-0 left-0 right-0 overflow-hidden break-words px-4 py-3 ${textSize} leading-relaxed text-ink-soft/50 transition-opacity duration-300 ${rotatingVisible ? "opacity-100" : "opacity-0"}`}
              >
                {rotatingPlaceholder}
              </div>
            )}
          </div>
          {(evidence.length > 0 || uploadError || uploadStatus) && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="evidence-tray">
              {evidence.map((ev) => (
                <span
                  key={ev.evidence_id}
                  data-testid="evidence-chip"
                  title={`${ev.kind} — read via ${ev.processor}${ev.scope_note ? " · " + ev.scope_note : ""}`}
                  className="inline-flex items-center gap-1.5 rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/5 px-2 py-0.5 text-[11px] text-[#F59E0B]"
                >
                  <span className="font-mono">{ev.kind}</span>
                  <span className="text-ink-soft max-w-[160px] truncate">{ev.filename}</span>
                  <button
                    onClick={() => onRemoveEvidence && onRemoveEvidence(ev.evidence_id)}
                    data-testid="evidence-remove"
                    aria-label="Remove attachment"
                    className="wat-focus text-ink-soft hover:text-ink"
                  >
                    ×
                  </button>
                </span>
              ))}
              {uploadStatus && (
                <span
                  data-testid="upload-progress"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/5 px-2 py-0.5 text-[11px] text-[#F59E0B]"
                >
                  <span className="font-mono">↑ {uploadStatus.current}/{uploadStatus.total}</span>
                  <span className="text-ink-soft max-w-[160px] truncate">{uploadStatus.name}</span>
                </span>
              )}
              {uploadError && (
                <span className="text-[11px] text-red-400" data-testid="upload-error">
                  {uploadError}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="wat-slot">
          {addVisible && (
            <div className="wat-ctl">
              <span className={`wat-vlabel wat-lbl-above${pulseCls}`} style={lbl}>Add</span>
              <button
                type="button"
                onClick={() => fileRef.current && fileRef.current.click()}
                disabled={busy || uploading}
                data-testid="upload-button"
                aria-label="Add attachment"
                title="Add attachment"
                className="wat-focus wat-plus disabled:opacity-40"
              >
                {uploading ? "…" : "+"}
              </button>
            </div>
          )}
        </div>

        <div className="wat-slot">
          <div className="wat-ctl">
            <button
              type="button"
              onClick={onSubmit}
              disabled={!canSubmit || busy}
              data-testid="nav-enter"
              aria-label="Enter"
              title="Enter"
              className="wat-focus wat-tri-btn"
            >
              <span className="wat-tri wat-tri-right" />
            </button>
            <span className={`wat-vlabel wat-lbl-below${pulseCls}`} style={lbl}>Enter</span>
          </div>
        </div>
      </div>

      <span className={`wat-conn${deeperVisible ? "" : " wat-conn-hidden"}`} />

      {/* ▼ GO DEEPER — the next layer's opening explanation previews ABOVE the
          control (under the prompt); tapping ▼ reveals the full explanation. */}
      <div className="wat-vslot">
        {deeperVisible && deeperPreview && (
          <span className="wat-preview" data-testid="deeper-preview">{deeperPreview}</span>
        )}
        <button
          type="button"
          onClick={onDeeper}
          disabled={!deeperVisible}
          data-testid="wat-next"
          aria-label="Go deeper"
          title="Go deeper"
          className={`wat-focus wat-tri-btn${deeperVisible ? "" : " invisible"}`}
        >
          <span className="wat-tri wat-tri-down" />
        </button>
        <span className={`wat-vlabel${pulseCls}`} style={lbl}>{deeperVisible ? "Go Deeper" : ""}</span>
      </div>

      <span className="wat-conn" />

      {/* ? — Save / Load WAT (gold action-control). Submission stays on ▶ Enter
          and the Enter key; ? no longer submits. */}
      <div className="wat-vslot">
        <button
          type="button"
          onClick={onSaveLoad || onSubmit}
          disabled={onSaveLoad ? false : (!canSubmit || busy)}
          data-testid={onSaveLoad ? "save-load-wat" : submitTestId}
          aria-label={onSaveLoad ? "Save or load WAT" : "Ask"}
          title={onSaveLoad ? "Save / Load WAT" : "Ask"}
          className="wat-focus wat-ask"
        >
          ?
        </button>
        <span className={`wat-vlabel${pulseCls}`} style={lbl}>{onSaveLoad ? "Save/Load" : "Ask"}</span>
      </div>
    </div>
  );
}


function App() {
  const _persist = loadSession();
  const [phase, setPhase] = useState(_persist?.phase === "thread" && _persist.turns.length ? "thread" : "home");
  const [question, setQuestion] = useState("");
  const [caseId, setCaseId] = useState(_persist?.caseId || null);
  const [turns, setTurns] = useState(_persist?.turns || []); // {answer, proactions, belief_change, depth_available, level, receipt}
  const [busy, setBusy] = useState(false);
  const [followup, setFollowup] = useState("");
  const [error, setError] = useState(null);
  const [lastExportSig, setLastExportSig] = useState(_persist?.lastExportSig ?? null);
  const [whyEver, setWhyEver] = useState(false);
  const [nextEver, setNextEver] = useState(false);
  const [whySuppressed, setWhySuppressed] = useState(!!_persist?.whySuppressed); // hide ▲ Why once its explanation stops changing
  const [lastWhyText, setLastWhyText] = useState(_persist?.lastWhyText ?? null); // last receipt the user actually saw
  const [leaveModal, setLeaveModal] = useState(null); // null | "ask" | "confirm"
  const [evidence, setEvidence] = useState([]); // pending uploaded evidence for next turn
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null); // { current, total, name } while uploading
  const [dragActive, setDragActive] = useState(false); // window-wide drag & drop overlay
  const [dropNote, setDropNote] = useState(null); // transient guidance for rejected drops
  const dropNoteTimer = useRef(null);
  const [familiarity, setFamiliarity] = useState(readFam()); // UI-only, persists
  const [starter, setStarter] = useState(pickStarter); // pristine placeholder
  const starterStageCommitted = useRef(null);
  // Composer tutorial (display-only overlay). `phText` is the current overlay
  // string; it NEVER becomes actual textarea input. The home + thread tutorial
  // effects below drive it. The thread tutorial tracks which instructions have
  // already been shown (session-scoped) so it runs ONCE and never restarts.
  const threadShownRef = useRef(null);
  if (threadShownRef.current === null) {
    let init = new Set();
    try {
      const raw = sessionStorage.getItem(THREAD_TUT_SHOWN_KEY);
      if (raw) init = new Set(raw.split(",").filter(Boolean));
    } catch { /* storage may be disabled */ }
    threadShownRef.current = init;
  }
  const [threadTutDone, setThreadTutDone] = useState(() => {
    try { return sessionStorage.getItem(THREAD_TUT_KEY) === "1"; } catch { return false; }
  });
  const [phText, setPhText] = useState("");
  const [phVisible, setPhVisible] = useState(true);
  useEffect(() => {
    // StrictMode may invoke the initializer and this effect twice in preview.
    // Advance only after a starter is committed, once per displayed suggestion.
    if (starterStageCommitted.current === starter) return;
    starterStageCommitted.current = starter;
    try {
      if (starter === PROPOSAL_SEQUENCE[0]) localStorage.setItem(PROPOSAL_STAGE_KEY, "1");
      else if (starter === PROPOSAL_SEQUENCE[1]) localStorage.setItem(PROPOSAL_STAGE_KEY, "2");
      else if (starter === PROPOSAL_SEQUENCE[2]) localStorage.setItem(PROPOSAL_STAGE_KEY, "0");
    } catch { /* storage may be disabled */ }
  }, [starter]);
  const [deeperEver, setDeeperEver] = useState(false); // gate: ◀ Left out reveals only after a Go Deeper
  const [leftOutShown, setLeftOutShown] = useState(_persist?.leftOutShown || 0); // revealed left-out branches (1 per tap, ≤3)
  const [deleteModal, setDeleteModal] = useState(false);
  // Phase 5 — encrypted .wat save/restore
  const [watModal, setWatModal] = useState(null); // null | "export" | "import"
  const [watMenu, setWatMenu] = useState(false); // gold ? Save/Load chooser
  const [pass1, setPass1] = useState("");
  const [pass2, setPass2] = useState("");
  const [watBusy, setWatBusy] = useState(false);
  const [watError, setWatError] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [exportPreview, setExportPreview] = useState(null); // { payload, counts }
  const bottomRef = useRef(null);

  // Fix session loss — persist the conversation locally so a refresh restores
  // the full thread, layers, branches, and Why/nav state.
  useEffect(() => {
    saveSession({ phase, caseId, turns, whySuppressed, lastWhyText, leftOutShown, lastExportSig });
  }, [phase, caseId, turns, whySuppressed, lastWhyText, leftOutShown, lastExportSig]);

  // Warn before refresh/close when there is unsaved work. Browsers only show a
  // generic prompt and MAY suppress it — an in-app reminder also appears.
  useEffect(() => {
    const dirty = turns.length > 0 && sigOf(turns) !== lastExportSig;
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [turns, lastExportSig]);

  // Home-screen widget deep links (Android). The widget carries NO conversation
  // text — it only routes a tap to a WAT action via ?wat_action=. Nothing here
  // ever submits an empty prompt; it opens / focuses the composer at most.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const act = params.get("wat_action") || (params.get("compose") ? "compose" : null);
    if (!act) return undefined;
    try { window.history.replaceState({}, "", window.location.pathname); } catch { /* ignore */ }
    const focusComposer = () => {
      const el = document.querySelector('[data-testid="followup-input"], [data-testid="question-input"]');
      if (el) {
        el.focus();
        try { el.setSelectionRange(el.value.length, el.value.length); } catch { /* not selectable */ }
      }
    };
    const t = setTimeout(() => {
      if (act === "add") {
        const up = document.querySelector('[data-testid="upload-button"]');
        if (up) up.click();
        else focusComposer();
      } else if (act === "compose" || act === "enter") {
        focusComposer(); // ready to type & send — never auto-submits
      }
      // "back" / "launch" simply open WAT (any existing session is already restored)
    }, 350);
    return () => clearTimeout(t);
  }, []);

  // Keyboard-safe composer (mobile). The native viewport directive
  // `interactive-widget=resizes-content` (index.html) does the heavy lifting on
  // Android Chrome — the layout resizes above the software keyboard so the
  // composer stays visible. This is a thin progressive-enhancement fallback for
  // browsers WITHOUT that support: on focus, ONCE, if the field is actually
  // covered by the keyboard (measured against visualViewport), gently bring it
  // into view. No persistent scroll/resize listeners → no scroll/focus loops,
  // and nothing happens when the layout already keeps the field visible.
  useEffect(() => {
    const isComposer = (el) =>
      el && el.matches && el.matches('[data-testid="question-input"],[data-testid="followup-input"]');
    const onFocusIn = (e) => {
      const el = e.target;
      if (!isComposer(el)) return;
      // Let the keyboard finish animating before measuring the visible area.
      setTimeout(() => {
        if (document.activeElement !== el) return; // focus moved on — do nothing
        const vv = window.visualViewport;
        const visibleTop = vv ? vv.offsetTop : 0;
        const visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
        const rect = el.getBoundingClientRect();
        const obscured = rect.bottom > visibleBottom - 8 || rect.top < visibleTop;
        if (!obscured) return; // layout already handles this device correctly
        const target = el.closest(".wat-prompt") || el;
        try { target.scrollIntoView({ block: "center", behavior: "smooth" }); }
        catch { target.scrollIntoView(); }
      }, 300);
    };
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, []);


  // ---- Phase 5: encrypted .wat save / restore (all crypto is client-side) ----
  const openExportWat = () => {
    setWatMenu(false);
    if (!turns.length) return;
    const nav = { leftOutShown, whySuppressed, lastWhyText };
    const { payload, counts } = redactPayload(buildPortable(turns, nav));
    setExportPreview({ payload, counts });
    setPass1(""); setPass2(""); setWatError(null); setWatModal("export");
  };

  const doExportWat = async () => {
    if (!exportPreview) return;
    if (pass1.length < 12) { setWatError("Use a passphrase of at least 12 characters."); return; }
    if (pass1 !== pass2) { setWatError("The two passphrases don't match."); return; }
    setWatBusy(true); setWatError(null);
    try {
      const fileText = await encryptWat(exportPreview.payload, pass1);
      const blob = new Blob([fileText], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `WAT_${(caseId || "session").slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.wat`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setLastExportSig(sigOf(turns)); // mark this state as saved
      setPass1(""); setPass2(""); setExportPreview(null); setWatModal(null);
    } catch (e) {
      setWatError(e instanceof WatError ? e.message : "Could not create the encrypted file.");
    } finally {
      setWatBusy(false);
    }
  };

  const openImportWat = () => {
    setWatMenu(false);
    setPass1(""); setImportFile(null); setWatError(null); setWatModal("import");
  };

  // Open the import dialog pre-loaded with a dropped .wat file. Nothing is
  // imported until the user enters the passphrase and confirms.
  const openImportWatWithFile = (file) => {
    setWatMenu(false);
    setPass1(""); setImportFile(file); setWatError(null); setWatModal("import");
  };

  const flashDropNote = (msg) => {
    setDropNote(msg);
    if (dropNoteTimer.current) clearTimeout(dropNoteTimer.current);
    dropNoteTimer.current = setTimeout(() => setDropNote(null), 4500);
  };

  const isWatFile = (f) => /\.wat$/i.test(f.name || "");

  const onWindowDragOver = (e) => {
    if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files")) {
      e.preventDefault();
      if (!dragActive) setDragActive(true);
    }
  };
  const onWindowDragLeave = (e) => {
    e.preventDefault();
    // Only clear when the cursor actually leaves the window, not on child boundaries.
    if (
      e.clientX <= 0 || e.clientY <= 0 ||
      e.clientX >= window.innerWidth || e.clientY >= window.innerHeight
    ) {
      setDragActive(false);
    }
  };
  const onWindowDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
    if (!files.length) return; // ignore dropped text / links / URLs
    const wats = files.filter(isWatFile);
    const others = files.filter((f) => !isWatFile(f));
    if (wats.length) {
      if (others.length) {
        flashDropNote("Drop a .wat file on its own — it can't be combined with other attachments.");
        return;
      }
      if (wats.length > 1) {
        flashDropNote("Import one .wat file at a time.");
        return;
      }
      openImportWatWithFile(wats[0]);
      return;
    }
    // Only regular attachments from here — attach to the pending question,
    // whether we're on home (first question) or inside a thread.
    onUpload(files);
  };

  const doImportWat = async () => {
    if (!importFile) { setWatError("Choose a .wat file to import."); return; }
    if (!pass1) { setWatError("Enter the passphrase for this file."); return; }
    setWatBusy(true); setWatError(null);
    try {
      const text = await importFile.text();
      let payload;
      if (isLegacyWat(text)) {
        // Older WAT-ENC v1 file — decrypt client-side, then map to the current
        // portable shape the backend import understands.
        const legacyObj = await decryptLegacyWat(text, pass1);
        payload = legacyToPortable(legacyObj);
      } else {
        payload = await decryptWat(text, pass1); // throws on wrong passphrase / corruption
      }
      if (!payload || payload.format !== "wat.portable" || !Array.isArray(payload.turns) || !payload.turns.length) {
        throw new WatError("This file doesn't contain a WAT conversation.");
      }
      const restored = turnsFromPortable(payload);
      // Materialize a NEW backend thread so the restored conversation can be continued.
      let newCaseId = null;
      try {
        const res = await watImport({ version: payload.version || 1, turns: payload.turns, nav: payload.nav || {} });
        newCaseId = res.case_id;
      } catch { /* view still restores; a follow-up would then start a fresh thread */ }
      const nav = payload.nav || {};
      setTurns(restored);
      setCaseId(newCaseId);
      setPhase("thread");
      setLeftOutShown(nav.leftOutShown || 0);
      setWhySuppressed(!!nav.whySuppressed);
      setLastWhyText(nav.lastWhyText ?? null);
      setDeeperEver(restored.some((t) => t.viaNext));
      setLastExportSig(sigOf(restored)); // freshly imported == saved
      setPass1(""); setImportFile(null); setWatModal(null); setError(null);
    } catch (e) {
      setWatError(e instanceof WatError ? e.message : "Could not import this file. Nothing was changed.");
    } finally {
      setWatBusy(false);
    }
  };

  // UI-only: bump interface familiarity (never affects canonical WAT state).
  const bumpFam = () =>
    setFamiliarity((f) => {
      const n = f + 1;
      try { localStorage.setItem(FAM_KEY, String(n)); } catch { /* ignore */ }
      return n;
    });

  // ◀ LEFT OUT — descend into a deeper BRANCH the reasoner considered but did
  // NOT take as the primary path. It IS a go-deeper action (viaNext); the
  // backend stays authoritative on whether it earns a new WAT depth.
  const submitLeftOut = async (text) => {
    if (!text || !caseId || busy) return;
    setBusy(true);
    setError(null);
    setDeeperEver(true);
    bumpFam();
    try {
      pushTurn(await watAnswer({ case_id: caseId, question: text, deeper: true }), "", { viaNext: true });
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  // (Auto-scroll removed) — after submission/render the viewport stays where
  // the user left it. Only user-initiated actions may move the viewport.

  const doArtifact = async (fmt) => {
    if (!caseId || !turns.length) return false;
    const ext = fmt === "md" ? "md" : fmt;
    const mime =
      fmt === "pdf"
        ? "application/pdf"
        : fmt === "docx"
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : fmt === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : fmt === "csv"
        ? "text/csv;charset=utf-8"
        : "text/markdown;charset=utf-8";
    try {
      const blob = await watArtifact(caseId, fmt);
      const url = URL.createObjectURL(new Blob([blob], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `WAT_${caseId.slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setLastExportSig(sigOf(turns));
      setWatMenu(false);
      return true;
    } catch (e) {
      setError(errText(e));
      return false;
    }
  };
  // Leaving without exporting: offer a real PDF artifact.
  const doExport = () => doArtifact("pdf");

  const level = turns.length ? turns[turns.length - 1].level : 0;

  // Materially-new understanding vs the immediately preceding entry, judged
  // ONLY from existing WAT signals (earned depth, a new deeper opportunity, a
  // new consequence/Proaction, or a belief change) — never from wording and
  // never by exposing internal reasoning.
  const materiallyNew = (cur, prev) => {
    if (!cur) return false;
    if (!prev) return true;
    if (cur.belief_change) return true;
    if ((cur.proactions?.length || 0) > (prev.proactions?.length || 0)) return true;
    if ((cur.level || 0) > (prev.level || 0)) return true;
    if (cur.depth_available && cur.depth_topic && cur.depth_topic !== prev.depth_topic) return true;
    return false;
  };

  const pushTurn = (data, userInput = "", extra = {}) => {
    setLeftOutShown(0);
    // A NEW "why" is warranted only when something actually changed — a belief
    // change or a genuinely deeper layer. Re-enable the Why button in that case.
    const _prevLast = turns.length ? turns[turns.length - 1] : null;
    if (data.belief_change || (typeof data.level === "number" && data.level > (_prevLast?.level || 0))) {
      setWhySuppressed(false);
    }
    setTurns((prev) => [
      ...prev,
      {
        userInput,
        answer: data.answer,
        proactions: data.proactions || [],
        belief_change: data.belief_change || null,
        depth_available: data.depth_available,
        depth_topic: data.depth_topic,
        level: data.level,
        continuations: data.continuations || [],
        deeper_alternatives: data.deeper_alternatives || [],
        invalidates_depth: data.invalidates_depth || 0,
        structured_blocks: data.structured_blocks || [],
        web_sources: data.web_sources || [],
        prevLevel: prev.length ? prev[prev.length - 1].level : 0,
        receipt: null,
        ...extra,
      },
    ]);
  };

  const start = async () => {
    if (!question.trim()) return;
    setBusy(true);
    setError(null);
    setPhase("thread");
    setTurns([]);
    setLeftOutShown(0);
    bumpFam();
    try {
      const ids = evidence.map((e) => e.evidence_id);
      const attached = evidence.map((e) => ({ name: e.filename, kind: e.kind }));
      const data = await watAnswer({ question: question.trim(), evidence_ids: ids });
      setCaseId(data.case_id);
      pushTurn(data, question.trim(), attached.length ? { attached } : {});
      setEvidence([]);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const goDeeper = async () => {
    if (!caseId || busy) return;
    setBusy(true);
    setError(null);
    setDeeperEver(true);
    bumpFam();
    try {
      pushTurn(await watAnswer({ case_id: caseId, deeper: true }), "", { viaNext: true });
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const askFollowup = async () => {
    if (!followup.trim() || !caseId || busy) return;
    const q = followup.trim();
    setFollowup("");
    setBusy(true);
    setError(null);
    bumpFam();
    try {
      const ids = evidence.map((e) => e.evidence_id);
      const attached = evidence.map((e) => ({ name: e.filename, kind: e.kind }));
      pushTurn(
        await watAnswer({ case_id: caseId, question: q, evidence_ids: ids }),
        q,
        attached.length ? { attached } : {}
      );
      setEvidence([]);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  // Slice 3 §12 — submit a chosen continuation as a normal thread turn. It is
  // NOT a "deeper" request: the backend stays authoritative on whether new
  // depth is earned, so selecting a continuation never forces WAT^(n+1).
  const submitContinuation = async (text) => {
    if (!text || !caseId || busy) return;
    setBusy(true);
    setError(null);
    bumpFam();
    try {
      const ids = evidence.map((e) => e.evidence_id);
      const attached = evidence.map((e) => ({ name: e.filename, kind: e.kind }));
      pushTurn(
        await watAnswer({ case_id: caseId, question: text, evidence_ids: ids }),
        text,
        attached.length ? { attached } : {}
      );
      setEvidence([]);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length || uploading) return;
    setUploading(true);
    setUploadError(null);
    bumpFam();
    const errors = [];
    // Sequential — one paid media read at a time, with per-file progress.
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      setUploadStatus({ current: i + 1, total: files.length, name: f.name });
      try {
        const res = await watUpload(f);
        if (res.error) errors.push(`${f.name}: ${res.scope_note || "could not process"}`);
        else setEvidence((prev) => [...prev, res]);
      } catch (e) {
        errors.push(`${f.name}: ${errText(e)}`);
      }
    }
    setUploadStatus(null);
    setUploading(false);
    if (errors.length) setUploadError(errors.join(" · "));
  };

  const removeEvidence = (id) =>
    setEvidence((prev) => prev.filter((e) => e.evidence_id !== id));

  const showReceipt = async (idx) => {
    let text;
    try {
      const { receipt } = await watReceipt(caseId, "Why?");
      text = (receipt || "").trim() || "I based that on the information you gave me in this conversation.";
    } catch (e) {
      text = "I based that on the information you gave me in this conversation.";
    }
    setTurns((prev) => prev.map((t, i) => (i === idx ? { ...t, receipt: text } : t)));
    // If this explanation is identical to the last one the user already saw, the
    // "why" changed nothing → stop offering the Why button until something
    // actually changes (belief change / deeper layer re-enables it in pushTurn).
    setWhySuppressed(lastWhyText != null && text === lastWhyText);
    setLastWhyText(text);
  };

  const reset = () => {
    setPhase("home");
    setQuestion("");
    setCaseId(null);
    setTurns([]);
    setFollowup("");
    setBusy(false);
    setError(null);
    setLastExportSig(null);
    setWhyEver(false);
    setNextEver(false);
    setWhySuppressed(false);
    setLastWhyText(null);
    setLeaveModal(null);
    setEvidence([]);
    setUploading(false);
    setUploadError(null);
    setDeeperEver(false);
    setLeftOutShown(0);
    setStarter(pickStarter());
  };

  // --- Canonical instrument state derived from the latest turn ---
  const last = turns.length ? turns[turns.length - 1] : null;
  const whyVisible =
    phase === "thread" && !busy && !!last && last.receipt == null &&
    materiallyNew(last, turns[turns.length - 2]) && !whySuppressed;
  const nextVisible =
    phase === "thread" && !busy && !!last && !!last.depth_available;

  // ◀ LEFT OUT availability — only after at least one Go Deeper, and only when
  // the current turn actually carries left-out deeper branches. Each tap reveals
  // one more (up to 3); clicking a branch descends into it.
  const leftOutItems = last?.deeper_alternatives || [];
  const leftOutCap = Math.min(3, leftOutItems.length);
  const leftOutVisible =
    phase === "thread" && !busy && deeperEver && leftOutCap > 0;
  const revealLeftOut = () => {
    bumpFam();
    setLeftOutShown((n) => Math.min(leftOutCap, n + 1));
  };

  // Once a control has genuinely appeared, it may be taught by the legend.
  useEffect(() => { if (whyVisible) setWhyEver(true); }, [whyVisible]);
  useEffect(() => { if (nextVisible) setNextEver(true); }, [nextVisible]);

  // ── HOME composer tutorial (display-only, once per session) ──────────────
  // Runs while the home composer is EMPTY: a quick typing demonstration
  // (§2) → hold the completed 3-line example → the GOLD action tutorial for
  // 10s (§4) → blank forever (§5). Never populates the textarea. User typing
  // wins instantly (§3) and marks the sequence done so it never restarts.
  useEffect(() => {
    if (phase !== "home") return undefined;
    if (question) {
      // User is typing → hide the demonstration, mark done, never restart.
      setPhText(""); setPhVisible(false);
      try { sessionStorage.setItem(HOME_TUT_KEY, "1"); } catch { /* ignore */ }
      return undefined;
    }
    let done = false;
    try { done = sessionStorage.getItem(HOME_TUT_KEY) === "1"; } catch { /* ignore */ }
    if (done) { setPhText(""); setPhVisible(false); return undefined; }

    const timers = [];
    const at = (fn, ms) => timers.push(setTimeout(fn, ms));

    // Build the typing frames. "It starts with " types quickly; "ONE" types
    // letter-by-letter; "thing...." appears immediately as a whole word; the
    // remaining two lines type letter-by-letter.
    const frames = [];
    let acc = "";
    for (const ch of "It starts with ") { acc += ch; frames.push({ t: acc, d: 30 }); }
    for (const ch of "ONE") { acc += ch; frames.push({ t: acc, d: 145 }); }
    acc += " thing....";
    frames.push({ t: acc, d: 380 });
    acc += "\n";
    for (const ch of "I don't know why") { acc += ch; frames.push({ t: acc, d: 55 }); }
    acc += "\n";
    for (const ch of "Linkin Park stopped touring?") { acc += ch; frames.push({ t: acc, d: 55 }); }

    setPhText(""); setPhVisible(true);
    let elapsed = 0;
    frames.forEach((f) => {
      elapsed += f.d;
      at(() => { setPhText(f.t); setPhVisible(true); }, elapsed);
    });

    const holdEnd = elapsed + 2000; // hold the completed 3-line example
    at(() => setPhVisible(false), holdEnd - 300);
    at(() => { setPhText(GOLD_INSTRUCTION); setPhVisible(true); }, holdEnd);
    at(() => setPhVisible(false), holdEnd + 10000); // gold tutorial visible 10s
    at(() => {
      setPhText(""); setPhVisible(false);
      try { sessionStorage.setItem(HOME_TUT_KEY, "1"); } catch { /* ignore */ }
    }, holdEnd + 10300);

    return () => timers.forEach(clearTimeout);
  }, [phase, question]);

  // ── THREAD composer tutorial (display-only, once per session) ────────────
  // While the thread composer is empty and idle, teach EACH AVAILABLE BUTTON
  // individually — ONE state per button, each shown at most once, using the
  // real live symbols: ▲ What changed, ▼ Go deeper, ◀ See what's left out, and
  // the gold − Delete / + Upload / ? Save/Load (NOT combined into a block). A
  // button is taught only when its control actually exists; unavailable ones
  // (e.g. ◀ before a Go Deeper) stay PENDING and are shown once later when they
  // first appear (and only when the composer is empty). Each button is tracked
  // independently in `threadShownRef`. When there are no untaught available
  // buttons the composer is blank; nothing ever replays or restarts (§9-10).
  const ALL_BTN_KEYS = ["why", "deeper", "left", "delete", "add", "save"];
  useEffect(() => {
    if (phase !== "thread") return undefined;
    if (threadTutDone) { setPhText(""); setPhVisible(false); return undefined; }
    // Typing wins; while thinking, controls are hidden — pause without marking.
    if (followup || busy) { setPhText(""); setPhVisible(false); return undefined; }

    const shown = threadShownRef.current;
    const persist = () => {
      try { sessionStorage.setItem(THREAD_TUT_SHOWN_KEY, Array.from(shown).join(",")); } catch { /* ignore */ }
    };
    // Order of instruction, each with its live availability. The gold −/+/? are
    // always present in the thread; ◀/▲/▼ depend on the current turn's state.
    const INSTR = [
      ["why", T_NAV_WHY, whyVisible],
      ["deeper", T_NAV_DEEPER, nextVisible],
      ["left", T_NAV_LEFT, leftOutVisible],
      ["delete", T_ACT_DELETE, true],
      ["add", T_ACT_ADD, true],
      ["save", T_ACT_SAVE, true],
    ];
    const pickNext = () => {
      for (const [k, txt, avail] of INSTR) if (avail && !shown.has(k)) return { key: k, text: txt };
      return null;
    };

    const timers = [];
    const showItem = (item) => {
      setPhText(item.text); setPhVisible(true);
      timers.push(setTimeout(() => {
        shown.add(item.key); persist();
        const nxt = pickNext();
        setPhVisible(false);
        timers.push(setTimeout(() => {
          if (nxt) { showItem(nxt); return; }
          setPhText("");
          // Fully complete (persist) only once EVERY button has been taught;
          // otherwise stay armed so a later-appearing button (e.g. ◀) can be
          // taught once when it first becomes available.
          if (ALL_BTN_KEYS.every((k) => shown.has(k))) {
            try { sessionStorage.setItem(THREAD_TUT_KEY, "1"); } catch { /* ignore */ }
            setThreadTutDone(true);
          }
        }, 300));
      }, 4500));
    };

    const first = pickNext();
    if (first) showItem(first);
    else { setPhText(""); setPhVisible(false); }

    return () => timers.forEach(clearTimeout);
  }, [phase, followup, busy, threadTutDone, whyVisible, nextVisible, leftOutVisible]);

  const legendItems = [
    { sym: "<!|", label: "upload" },
    { sym: "|>>", label: "enter" },
    ...(whyEver ? [{ sym: "/^\\", label: "why" }] : []),
    ...(nextEver ? [{ sym: "\\\\//", label: "WAT next" }] : []),
  ];

  const showLatestReceipt = () => { if (last) { bumpFam(); showReceipt(turns.length - 1); } };

  // − DELETE — backend-enforced hard delete of the current string, then pristine.
  const deleteCurrent = async () => {
    if (caseId) {
      try { await watDelete(caseId); } catch { /* still return to pristine */ }
    }
    setDeleteModal(false);
    reset();
  };

  // Export-freshness: dirty if the thread changed since the last successful export.
  const isDirty = turns.length > 0 && sigOf(turns) !== lastExportSig;
  const requestNew = () => { if (isDirty) setLeaveModal("ask"); else reset(); };
  const exportThenLeave = async () => {
    const ok = await doExport();
    if (ok) { setLeaveModal(null); reset(); }
  };

  return (
    <div
      className="min-h-screen"
      onDragOver={onWindowDragOver}
      onDragLeave={onWindowDragLeave}
      onDrop={onWindowDrop}
      data-testid="wat-window"
    >
      {dragActive && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-bone/70 backdrop-blur-sm pointer-events-none"
          data-testid="drag-overlay"
        >
          <div className="rounded-lg border-2 border-dashed border-[#F59E0B]/60 bg-surface/80 px-8 py-6 text-center">
            <p className="text-sm text-ink font-medium">Drop files to attach</p>
            <p className="mt-1 text-xs text-ink-soft">or drop a single .wat file to import a saved conversation</p>
          </div>
        </div>
      )}
      {dropNote && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] rounded-md border border-line bg-surface px-4 py-2 text-xs text-ink shadow-lg"
          data-testid="drop-note"
        >
          {dropNote}
        </div>
      )}
      {phase === "thread" && (
        <div className="sticky top-0 z-30 bg-bone/95 backdrop-blur border-b border-line" style={{ paddingTop: "env(safe-area-inset-top)" }}>
          <div className="max-w-2xl mx-auto px-6 py-3 relative flex items-center justify-center">
            <div className="absolute left-3 sm:left-6 flex items-center">
              <span
                data-testid="save-status"
                className={`text-[10px] font-mono uppercase tracking-widest ${isDirty ? "text-[#F59E0B]/90" : "text-ink-soft/60"}`}
              >
                {isDirty ? "Unsaved WAT" : "Saved"}
              </span>
            </div>
            <button onClick={requestNew} data-testid="home-reset" aria-label="WAT" className="wat-focus">
              <Brand className="text-base sm:text-xl" />
            </button>
            <div className="absolute right-3 sm:right-6 flex items-center gap-2 sm:gap-4">
              <DepthTrail level={level} />
              <button
                onClick={requestNew}
                data-testid="new-inquiry"
                aria-label="New"
                title="New"
                className="wat-focus text-[11px] text-ink-soft/70 hover:text-ink transition-colors"
              >
                New
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-6">
        <AnimatePresence mode="wait">
          {phase === "home" && (
            <motion.div
              key="home"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="min-h-screen flex flex-col justify-center py-12 sm:py-20"
            >
              <div className="mb-10 sm:mb-14 text-center">
                <Brand className="text-5xl sm:text-6xl" />
              </div>
              <Instrument
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onSubmit={start}
                size="home"
                autoFocus
                canSubmit={!!question.trim()}
                busy={busy}
                placeholder={starter}
                rotatingPlaceholder={phText}
                rotatingVisible={phVisible}
                onSaveLoad={() => setWatMenu(true)}
                inputTestId="question-input"
                submitTestId="ask-button"
                familiarity={familiarity}
                addVisible
                onUpload={onUpload}
                uploading={uploading}
                uploadError={uploadError}
                uploadStatus={uploadStatus}
                evidence={evidence}
                onRemoveEvidence={removeEvidence}
              />
              {busy && (
                <p className="mt-4 text-sm text-ink-soft text-center" data-testid="home-busy">
                  Thinking…
                </p>
              )}
              {error && <p className="mt-4 text-sm text-red-400 text-center">{error}</p>}
              <div className="mt-10 text-center">
                <button
                  onClick={openImportWat}
                  data-testid="home-import"
                  className="wat-focus text-xs text-ink-soft hover:text-ink transition-colors underline underline-offset-4 decoration-line/60"
                >
                  Import a saved .wat conversation
                </button>
              </div>
            </motion.div>
          )}

          {phase === "thread" && (
            <motion.div
              key="thread"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-12 space-y-10"
            >
              {turns.map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  data-testid={`turn-${i}`}
                  className="transition"
                >
                  {t.viaNext && (
                    <div className="mb-3 flex items-center justify-center" data-testid={`next-event-${i}`}>
                      <span className="font-mono text-[11px] tracking-[0.3em] text-red-400/80">{"\\\\//"}</span>
                    </div>
                  )}
                  {t.userInput && (
                    <div className="mb-4" data-testid={`user-input-${i}`}>
                      <span className="font-mono text-[11px] tracking-widest text-ink-soft">
                        YOU
                      </span>
                      <p className="mt-1 text-ink-soft italic whitespace-pre-wrap">
                        {t.userInput}
                      </p>
                    </div>
                  )}
                  {t.attached && t.attached.length > 0 && (
                    <div className="mb-4 flex flex-wrap gap-2" data-testid={`attached-${i}`}>
                      {t.attached.map((a, k) => (
                        <span
                          key={k}
                          className="inline-flex items-center gap-1.5 rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/5 px-2 py-0.5 text-[11px] text-[#F59E0B]"
                        >
                          <span className="font-mono">{a.kind}</span>
                          <span className="text-ink-soft max-w-[180px] truncate">{a.name}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="font-mono text-[11px] tracking-widest text-ink-soft">
                      {watMark(t.level)}
                    </span>
                  </div>

                  {t.invalidates_depth > 0 && t.prevLevel > t.level && (
                    <DepthDropCue from={t.prevLevel} to={t.level} />
                  )}

                  {t.belief_change && <BeliefBanner bc={t.belief_change} />}

                  <div
                    className="font-serif text-[19px] leading-relaxed text-ink whitespace-pre-wrap"
                    data-testid={`answer-${i}`}
                  >
                    {t.answer}
                  </div>

                  {(t.structured_blocks || []).map((tbl, j) => (
                    <StructuredTable key={j} tbl={tbl} i={i} j={j} />
                  ))}

                  <div id={`proaction-anchor-${i}`}>
                    {t.proactions.map((p, j) => (
                      <Proaction key={j} p={p} i={j} />
                    ))}
                  </div>

                  {t.web_sources && t.web_sources.length > 0 && (
                    <WebSources sources={t.web_sources} testid={`web-sources-${i}`} />
                  )}

                  {t.continuations && t.continuations.length > 0 && (
                    <ContinuationChips
                      items={t.continuations}
                      onPick={submitContinuation}
                      disabled={busy}
                      testid={`continuations-${i}`}
                    />
                  )}

                  {t.receipt && (
                    <div className="mt-3 border-l-2 border-red-500/40 pl-4 text-sm text-ink-soft leading-relaxed" data-testid={`receipt-${i}`}>
                      {t.receipt}
                    </div>
                  )}
                </motion.div>
              ))}

              {busy && <p className="text-sm text-ink-soft" data-testid="thread-busy">Thinking…</p>}
              {error && <p className="text-sm text-red-400">{error}</p>}

              {leftOutShown > 0 && leftOutItems.length > 0 && (
                <div className="pt-6" data-testid="left-out-panel">
                  <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-ink-soft/70">
                    Left out — other deeper directions not taken
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {leftOutItems.slice(0, leftOutShown).map((c, k) => (
                      <button
                        key={k}
                        type="button"
                        disabled={busy}
                        onClick={() => submitLeftOut(c)}
                        data-testid={`left-out-chip-${k}`}
                        className="rounded-full border border-red-500/30 px-3 py-1 text-[13px] text-ink-soft hover:text-ink hover:border-red-500/60 transition-colors disabled:opacity-40"
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* The WAT instrument: prompt-centered ◀ − [prompt] + ▶ with ▲ Why / ▼ Deeper / ? Ask on the axis */}
              <div className="pt-6 border-t border-line">
                <Instrument
                  value={followup}
                  onChange={(e) => setFollowup(e.target.value)}
                  onSubmit={askFollowup}
                  size="thread"
                  canSubmit={!!followup.trim()}
                  busy={busy}
                  inputTestId="followup-input"
                  submitTestId="followup-submit"
                  familiarity={familiarity}
                  rotatingPlaceholder={phText}
                  rotatingVisible={phVisible}
                  onSaveLoad={() => setWatMenu(true)}
                  whyVisible={whyVisible}
                  onWhy={showLatestReceipt}
                  deeperVisible={nextVisible}
                  onDeeper={goDeeper}
                  deeperPreview={last?.depth_topic || ""}
                  leftOutVisible={leftOutVisible}
                  onLeftOut={revealLeftOut}
                  deleteVisible={phase === "thread"}
                  onDelete={() => setDeleteModal(true)}
                  addVisible={phase === "thread"}
                  onUpload={onUpload}
                  uploading={uploading}
                  uploadError={uploadError}
                  evidence={evidence}
                  onRemoveEvidence={removeEvidence}
                  uploadStatus={uploadStatus}
                />
              </div>
              <div ref={bottomRef} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {leaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6" data-testid="leave-modal">
          <div className="max-w-sm w-full rounded-md bg-surface border border-line p-6 text-center">
            {leaveModal === "ask" ? (
              <>
                <p className="text-ink text-[15px] leading-relaxed">
                  Starting a new session won't erase this one automatically, but
                  it will leave it. Save an encrypted <span className="text-[#F59E0B]">.wat</span> first to keep it?
                </p>
                <div className="mt-6 flex flex-col items-center gap-2">
                  <button
                    onClick={() => { setLeaveModal(null); openExportWat(); }}
                    data-testid="leave-save-wat"
                    className="wat-focus w-full px-3 py-2 rounded-md text-sm text-[#F59E0B] border border-[#F59E0B]/50 hover:bg-[#F59E0B]/10 transition"
                  >
                    Save encrypted .wat
                  </button>
                  <button
                    onClick={exportThenLeave}
                    data-testid="leave-export-yes"
                    className="wat-focus w-full px-3 py-1.5 rounded-md text-sm text-ink-soft hover:text-ink transition"
                  >
                    Export a PDF instead
                  </button>
                  <button
                    onClick={() => setLeaveModal("confirm")}
                    data-testid="leave-export-no"
                    className="wat-focus w-full px-3 py-1.5 rounded-md text-sm text-ink-soft hover:text-ink transition"
                  >
                    Start new without saving
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-ink text-[15px] leading-relaxed">
                  Leave without exporting? This conversation will not be preserved by you.
                </p>
                <div className="mt-6 flex items-center justify-center gap-3">
                  <button
                    onClick={() => { setLeaveModal(null); reset(); }}
                    data-testid="leave-confirm"
                    className="wat-focus px-3 py-1.5 rounded-md text-sm text-red-400 border border-red-500/40 hover:bg-red-950/30 transition"
                  >
                    Leave without exporting
                  </button>
                  <button
                    onClick={() => setLeaveModal(null)}
                    data-testid="leave-cancel"
                    className="wat-focus px-3 py-1.5 rounded-md text-sm text-ink-soft hover:text-ink transition"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6" data-testid="delete-modal">
          <div className="max-w-sm w-full rounded-md bg-surface border border-line p-6 text-center">
            <p className="text-ink text-[15px] leading-relaxed">
              Delete this WAT string?
              <br />
              This permanently destroys it and everything it holds. It cannot be undone.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                onClick={deleteCurrent}
                data-testid="delete-confirm"
                className="wat-focus px-3 py-1.5 rounded-md text-sm text-red-400 border border-red-500/40 hover:bg-red-950/30 transition"
              >
                Delete permanently
              </button>
              <button
                onClick={() => setDeleteModal(false)}
                data-testid="delete-cancel"
                className="wat-focus px-3 py-1.5 rounded-md text-sm text-ink-soft hover:text-ink transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {watMenu && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" data-testid="wat-menu" style={{ paddingBottom: "env(safe-area-inset-bottom)", paddingTop: "env(safe-area-inset-top)" }}>
          <div className="max-w-sm w-full rounded-md bg-surface border border-line p-5">
            <h2 className="text-ink text-base font-medium mb-1">Save / Load WAT</h2>
            <p className="text-ink-soft text-xs leading-relaxed mb-4">
              Save the current WAT as an encrypted <span className="font-mono">.wat</span> file, or load a previously saved one. Nothing is saved automatically.
            </p>
            <div className="space-y-2">
              <button
                onClick={() => { setWatMenu(false); openExportWat(); }}
                disabled={turns.length === 0}
                data-testid="wat-menu-save"
                className="wat-focus block w-full rounded border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2 text-left text-sm text-[#F59E0B] hover:bg-[#F59E0B]/15 transition disabled:opacity-40"
              >
                Save current WAT (encrypted .wat)
              </button>
              <button
                onClick={() => { setWatMenu(false); openImportWat(); }}
                data-testid="wat-menu-load"
                className="wat-focus block w-full rounded border border-line bg-bone/40 px-3 py-2 text-left text-sm text-ink hover:bg-surfaceHover transition"
              >
                Load a saved WAT (.wat)
              </button>
              {turns.length > 0 && (
                <div className="pt-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-ink-soft mb-1">Export as document</div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => { setWatMenu(false); doArtifact("pdf"); }} data-testid="wat-menu-pdf" className="wat-focus rounded border border-line px-2 py-1 text-xs text-ink hover:bg-surfaceHover transition">PDF</button>
                    <button onClick={() => { setWatMenu(false); doArtifact("docx"); }} data-testid="wat-menu-docx" className="wat-focus rounded border border-line px-2 py-1 text-xs text-ink hover:bg-surfaceHover transition">Word</button>
                    <button onClick={() => { setWatMenu(false); doArtifact("md"); }} data-testid="wat-menu-md" className="wat-focus rounded border border-line px-2 py-1 text-xs text-ink-soft hover:bg-surfaceHover transition">Markdown</button>
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => setWatMenu(false)} data-testid="wat-menu-close" className="wat-focus mt-4 block w-full text-center text-xs text-ink-soft hover:text-ink transition">Cancel</button>
          </div>
        </div>
      )}
      {watModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" data-testid="wat-modal" style={{ paddingBottom: "env(safe-area-inset-bottom)", paddingTop: "env(safe-area-inset-top)" }}>
          <div className="max-w-md w-full max-h-[85vh] overflow-y-auto rounded-md bg-surface border border-line p-5">
            {watModal === "export" ? (
              <>
                <h2 className="text-ink text-base font-medium mb-1">Save this conversation</h2>
                <p className="text-ink-soft text-xs leading-relaxed mb-3">
                  Encrypted on your device with a passphrase you choose (AES-256-GCM). The passphrase is never stored or sent anywhere — if you lose it, the file can't be opened.
                </p>
                <div className="mb-3 rounded border border-line bg-bone/40 p-3" data-testid="export-preview">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-ink-soft mb-2">What will be saved</div>
                  <div className="text-[11px] text-[#F59E0B] mb-2" data-testid="redaction-summary">{redactionSummary(exportPreview?.counts || {})}</div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {(exportPreview?.payload.turns || []).map((t, k) => (
                      <div key={k} className="text-[11px] leading-snug">
                        {t.user_input && <div className="text-ink-soft italic truncate">You: {t.user_input}</div>}
                        <div className="text-ink truncate">{watMark(t.level)}: {t.envelope.answer || "(no answer)"}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <input type="password" value={pass1} onChange={(e) => setPass1(e.target.value)} placeholder="Passphrase (min 12 characters)" data-testid="export-pass1" className="wat-focus block w-full mb-2 rounded bg-bone/60 border border-line px-3 py-2 text-sm text-ink" />
                <input type="password" value={pass2} onChange={(e) => setPass2(e.target.value)} placeholder="Confirm passphrase" data-testid="export-pass2" className="wat-focus block w-full mb-3 rounded bg-bone/60 border border-line px-3 py-2 text-sm text-ink" />
                {watError && <p className="text-red-400 text-xs mb-3" data-testid="wat-error">{watError}</p>}
                <div className="flex items-center justify-end gap-3">
                  <button onClick={() => { setWatModal(null); setWatError(null); }} data-testid="wat-cancel" className="wat-focus px-3 py-1.5 text-sm text-ink-soft hover:text-ink transition">Cancel</button>
                  <button onClick={doExportWat} disabled={watBusy} data-testid="export-confirm" className="wat-focus px-3 py-1.5 rounded-md text-sm text-[#F59E0B] border border-[#F59E0B]/50 hover:bg-[#F59E0B]/10 transition disabled:opacity-40">{watBusy ? "Encrypting…" : "Download .wat"}</button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-ink text-base font-medium mb-1">Import a saved conversation</h2>
                <p className="text-ink-soft text-xs leading-relaxed mb-3">
                  Choose a .wat file and enter its passphrase. A wrong passphrase or a damaged file is rejected — nothing partial is ever imported.
                </p>
                <input type="file" accept=".wat,application/octet-stream" onChange={(e) => setImportFile(e.target.files && e.target.files[0])} data-testid="import-file" className="wat-focus block w-full mb-2 text-xs text-ink-soft file:mr-3 file:rounded file:border file:border-line file:bg-bone/60 file:px-3 file:py-1.5 file:text-ink" />
                {importFile && (
                  <p className="mb-3 text-[11px] text-[#F59E0B]" data-testid="import-file-name">Selected: {importFile.name}</p>
                )}
                <input type="password" value={pass1} onChange={(e) => setPass1(e.target.value)} placeholder="Passphrase" data-testid="import-pass" className="wat-focus block w-full mb-3 rounded bg-bone/60 border border-line px-3 py-2 text-sm text-ink" />
                {watError && <p className="text-red-400 text-xs mb-3" data-testid="wat-error">{watError}</p>}
                <div className="flex items-center justify-end gap-3">
                  <button onClick={() => { setWatModal(null); setWatError(null); }} data-testid="wat-cancel" className="wat-focus px-3 py-1.5 text-sm text-ink-soft hover:text-ink transition">Cancel</button>
                  <button onClick={doImportWat} disabled={watBusy} data-testid="import-confirm" className="wat-focus px-3 py-1.5 rounded-md text-sm text-ink border border-line hover:bg-surfaceHover transition disabled:opacity-40">{watBusy ? "Decrypting…" : "Import"}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
